import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Parse } from '../../lib/parse';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { useAlert } from '../../contexts/AlertContext';

const BrandingSettings = ({ tenantId, isAdmin }) => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [branding, setBranding] = useState({
    appName: 'Digicert',
    headerBarColor: '#ef4b26',
    primaryColor: '#ef4b26',
    secondaryColor: '#6b7280',
    loginLogo: '',
    headerLogo: '',
    faviconUrl: '',
    documentationUrl: ''
  });

  useEffect(() => {
    if (!isAdmin || !tenantId) return;
    loadBrandingConfig();
  }, [tenantId, isAdmin]);

  const loadBrandingConfig = async () => {
    try {
      setIsLoading(true);
      const config = await Parse.Cloud.run('getbrandingconfig', {
        tenantId
      });
      if (config) {
        setBranding(config);
      }
    } catch (error) {
      console.error('Error loading branding config:', error);
      showError(error?.message || 'Failed to load branding configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setBranding(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleColorChange = (field, value) => {
    if (/^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(value) || value === '') {
      handleInputChange(field, value);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await Parse.Cloud.run('updatebrandingconfig', {
        tenantId,
        ...branding
      });
      showSuccess(t('settings.app-branding.saved', { defaultValue: 'App branding configuration saved successfully' }));
      loadBrandingConfig();
    } catch (error) {
      console.error('Error saving branding config:', error);
      showError(error?.message || 'Failed to save branding configuration');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  if (isLoading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">
          {t('settings.app-branding.title', { defaultValue: 'App Branding Configuration' })}
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          {t('settings.app-branding.description', { defaultValue: 'Customize the appearance of your application header, logo, and colors' })}
        </p>
      </div>

      {/* App Name */}
      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.branding.appName', { defaultValue: 'Application Name' })}
        </label>
        <Input
          type="text"
          value={branding.appName}
          onChange={(e) => handleInputChange('appName', e.target.value)}
          placeholder="e.g., Digicert"
        />
      </div>

      {/* Colors */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            {t('settings.branding.headerBarColor', { defaultValue: 'Header Bar Color' })}
          </label>
          <div className="flex gap-2">
            <input
              type="color"
              value={branding.headerBarColor}
              onChange={(e) => handleColorChange('headerBarColor', e.target.value)}
              className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
            />
            <Input
              type="text"
              value={branding.headerBarColor}
              onChange={(e) => handleColorChange('headerBarColor', e.target.value)}
              placeholder="#ef4b26"
              className="flex-1"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            {t('settings.branding.primaryColor', { defaultValue: 'Primary Color' })}
          </label>
          <div className="flex gap-2">
            <input
              type="color"
              value={branding.primaryColor}
              onChange={(e) => handleColorChange('primaryColor', e.target.value)}
              className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
            />
            <Input
              type="text"
              value={branding.primaryColor}
              onChange={(e) => handleColorChange('primaryColor', e.target.value)}
              placeholder="#ef4b26"
              className="flex-1"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            {t('settings.branding.secondaryColor', { defaultValue: 'Secondary Color' })}
          </label>
          <div className="flex gap-2">
            <input
              type="color"
              value={branding.secondaryColor}
              onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
              className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
            />
            <Input
              type="text"
              value={branding.secondaryColor}
              onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
              placeholder="#6b7280"
              className="flex-1"
            />
          </div>
        </div>
      </div>

      {/* Logo URLs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            {t('settings.branding.loginLogo', { defaultValue: 'Login Logo URL' })}
          </label>
          <Input
            type="text"
            value={branding.loginLogo}
            onChange={(e) => handleInputChange('loginLogo', e.target.value)}
            placeholder="https://example.com/logo.png"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            {t('settings.branding.headerLogo', { defaultValue: 'Header Logo URL' })}
          </label>
          <Input
            type="text"
            value={branding.headerLogo}
            onChange={(e) => handleInputChange('headerLogo', e.target.value)}
            placeholder="https://example.com/logo-white.png"
          />
        </div>
      </div>

      {/* Favicon */}
      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.branding.faviconUrl', { defaultValue: 'Favicon URL' })}
        </label>
        <Input
          type="text"
          value={branding.faviconUrl}
          onChange={(e) => handleInputChange('faviconUrl', e.target.value)}
          placeholder="https://example.com/favicon.ico"
        />
      </div>

      {/* Documentation URL */}
      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.branding.documentationUrl', { defaultValue: 'Documentation URL' })}
        </label>
        <Input
          type="text"
          value={branding.documentationUrl}
          onChange={(e) => handleInputChange('documentationUrl', e.target.value)}
          placeholder="https://example.com/docs"
        />
        <p className="text-xs text-gray-500 mt-1">
          {t('settings.branding.documentationUrlHint', { defaultValue: 'URL for the help/documentation link in the header. Leave empty to use the default.' })}
        </p>
      </div>

      {/* Save Button */}
      <div className="flex gap-2 pt-4">
        <Button
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? t('saving', { defaultValue: 'Saving...' }) : t('save', { defaultValue: 'Save' })}
        </Button>
        <Button
          variant="ghost"
          onClick={loadBrandingConfig}
          disabled={isSaving}
        >
          {t('cancel', { defaultValue: 'Cancel' })}
        </Button>
      </div>
    </div>
  );
};

export default BrandingSettings;
